/* ============================================================
 * frontend-v3/src/screens/channels/ChannelDestinations.tsx
 *
 * Channel "Ajustes → Destinos" — the single canonical editor for a
 * channel's output destination config.
 *
 * NMW-233 / NMW-228: a "destination" is not its own entity — it is the
 * channel's output config (`outputDestination` + `youtubeStreamKey` on the
 * `channels` row). Writes go through the proven, auth-gated
 * `api.channels.updateChannel` (the same writer ChannelEdit uses).
 *
 * NMW-319: three YouTube identifiers are managed here, structurally, to kill
 * the hand-typed-key error class:
 *   1. Account (OAuth) — pick a linked Nango connection (owner-scoped reads).
 *   2. liveStream — pick one of the account's real streams; the key
 *      (cdn.ingestionInfo.streamName) is resolved SERVER-SIDE (never typed),
 *      so youtubeStreamKey can never point at a non-existent stream. The
 *      stream id is stored to anchor broadcast resolution.
 *   3. Live broadcast id (youtubeVideoId) — informational only; see below.
 *
 * 2026-07-20 — ingest identity is now an EXPLICIT two-mode choice
 * (`channels.youtubeBindingMode`), not a sentinel hidden inside the stream
 * picker:
 *   • "account" — account + liveStream picked here; key and stream id are
 *     resolved from ONE liveStreams.list response, so they cannot drift apart.
 *   • "manual"  — a pasted raw key. FORCED (and now actually reachable) when no
 *     account is linked: previously this screen told the operator to link an
 *     account and exposed no key input at all, so channels created with a key
 *     and no account could not be edited here.
 * Switching to manual clears `youtubeLiveStreamId` SERVER-SIDE (channels.ts) —
 * the client is not trusted to keep the pair consistent.
 *
 * The live broadcast id is deliberately demoted to informational: a YouTube
 * liveStream is reusable, so every go-live mints a NEW video id and any pinned
 * value is stale after the first restart (measured: 11 broadcasts bound to one
 * stream on the dev account, 21 of 22 `complete`).
 *
 * Honest-state (CLAUDE.md §5): Skeleton while loading, EmptyState when the
 * route id matches no channel; real backend errors surface via the wrapper
 * toast / honest pills. argKeys-only logging per V3I-INV-12.
 * ============================================================ */
import { useState, useEffect, type MouseEvent } from 'react';
import { useQuery, useAction } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import {
  Btn,
  EmptyState,
  Field,
  Input,
  Pill,
  SegmentedControl,
  Select,
  Skeleton,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { navigate } from '../../lib/nav';
import { useChannelList } from '../../hooks/useChannelList';

type OutputDestination = 'youtube_live' | 'o_and_o' | 'both';

// The Select primitive treats a falsy option value as "use the object itself",
// so sentinels must be truthy strings.
const NO_CONNECTION = '__none__'; // account picker: "no account linked"
const NO_STREAM = '__pick__'; // stream picker: placeholder prompt (nothing bound)

interface NangoConnectionOption {
  connectionId: string;
  label: string;
  youtubeTitle: string | null;
  email: string | null;
  createdAt: string | null;
}

interface LiveStreamOption {
  streamId: string;
  title: string;
  streamStatus: string | null;
}

type BindingMode = 'account' | 'manual';

interface ChannelFull {
  outputDestination?: OutputDestination;
  youtubeStreamKey?: string;
  nangoYoutubeConnectionId?: string;
  youtubeLiveStreamId?: string;
  youtubeVideoId?: string;
  youtubeVideoIdPinned?: boolean;
  youtubeBindingMode?: BindingMode;
}

interface ChannelDestinationsProps {
  id?: string;
}

// Masked preview of a stored key: bullets + last 4 chars.
function maskKey(k: string): string {
  const key = k.trim();
  if (key.length <= 4) return '•'.repeat(key.length);
  const hidden = Math.min(key.length - 4, 8);
  return '•'.repeat(hidden) + key.slice(-4);
}

export function ChannelDestinations({ id }: ChannelDestinationsProps) {
  const rows = useChannelList();
  const real = rows?.find((x) => x._id === id || x.slug === id);
  const channelIdMaybe = real?._id as Id<'channels'> | undefined;

  const full = useQuery(
    api.channels.getChannelWithState,
    channelIdMaybe ? { channelId: channelIdMaybe } : 'skip',
  ) as ChannelFull | null | undefined;

  const updateM = useMutationWithToast(api.channels.updateChannel, {
    logLabel: 'channel-destinations-update',
    mutationName: 'channels.updateChannel',
    successMessage: 'Destinos actualizados',
  });
  const setLiveStream = useAction(api.actions.youtubeLiveStreams.setChannelLiveStream);
  const listConnections = useAction(api.actions.youtubeConnections.listYoutubeConnections);
  const listStreams = useAction(api.actions.youtubeLiveStreams.listYoutubeLiveStreams);

  const [form, setForm] = useState<{
    outputDestination?: OutputDestination;
    youtubeStreamKey?: string; // manual-fallback raw key
    nangoConnSel?: string; // undefined = untouched; NO_CONNECTION = unlink
    streamSel?: string; // undefined = untouched; a streamId
  }>({});
  const [saving, setSaving] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);

  // Effective YouTube account: the operator's live selection wins over the
  // saved value, so the liveStream picker refreshes as soon as a different
  // account is chosen (before saving).
  const linkedConn = full?.nangoYoutubeConnectionId?.trim() || '';
  const selectedConn =
    form.nangoConnSel === undefined
      ? linkedConn
      : form.nangoConnSel === NO_CONNECTION
        ? ''
        : form.nangoConnSel;

  // Account picker options (network action). connsReload re-runs the fetch on
  // demand (e.g. after linking a new account in Nango) without remounting.
  const [conns, setConns] = useState<NangoConnectionOption[] | null | undefined>(undefined);
  const [connsReload, setConnsReload] = useState(0);
  const [connsRefreshing, setConnsRefreshing] = useState(false);
  useEffect(() => {
    let alive = true;
    listConnections({})
      .then((r: NangoConnectionOption[]) => { if (alive) setConns(r); })
      .catch(() => { if (alive) setConns(null); })
      .finally(() => { if (alive) setConnsRefreshing(false); });
    return () => { alive = false; };
  }, [listConnections, connsReload]);

  // liveStream picker options — keyed on the EFFECTIVE account (saved or
  // freshly selected), so changing the account re-fetches the stream list.
  // An unsaved selection is passed as `connectionId` for a server-side preview.
  const [streams, setStreams] = useState<LiveStreamOption[] | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    if (!channelIdMaybe || !selectedConn) { setStreams(undefined); return; }
    setStreams(undefined); // skeleton while (re)loading for this account
    listStreams({
      channelId: channelIdMaybe,
      ...(selectedConn !== linkedConn ? { connectionId: selectedConn } : {}),
    })
      .then((r: LiveStreamOption[]) => { if (alive) setStreams(r); })
      .catch(() => { if (alive) setStreams(null); });
    return () => { alive = false; };
  }, [listStreams, channelIdMaybe, selectedConn, linkedConn]);

  if (rows === undefined) {
    return <Skeleton h={240} />;
  }
  if (real === undefined) {
    return (
      <EmptyState
        icon="alertTriangle"
        title="Canal no encontrado"
        body="No se localizó el canal solicitado."
      />
    );
  }

  const channelId = real._id as unknown as Id<'channels'>;
  const vOut: OutputDestination =
    form.outputDestination ?? full?.outputDestination ?? real.outputDestination ?? 'o_and_o';
  const needsKey = vOut === 'youtube_live' || vOut === 'both';

  // ---- Account picker derived state ----
  const connValue = form.nangoConnSel ?? (linkedConn || NO_CONNECTION);
  const connOptions: { value: string; label: string }[] = [
    { value: NO_CONNECTION, label: '— Sin cuenta vinculada —' },
    ...(conns ?? []).map((c) => ({ value: c.connectionId, label: c.label })),
  ];
  if (linkedConn && !connOptions.some((o) => o.value === linkedConn)) {
    connOptions.push({ value: linkedConn, label: `Cuenta ${linkedConn.slice(0, 8)}…` });
  }

  // ---- ingest-identity mode (schema.ts:youtubeBindingMode) ----
  // STRICT EITHER/OR — the mode is a CONSEQUENCE of whether an account is
  // linked, never an independent choice:
  //
  //   account linked  → pick one of that account's streams. The key is resolved
  //                     server-side from the same liveStreams.list response, so
  //                     account/key/stream-id provably agree.
  //   no account      → paste a raw key. (Reachable at all only since 2026-07-20;
  //                     this screen used to say "link an account first" and
  //                     expose no key input, stranding every channel created
  //                     with a key and no account.)
  //
  // There is deliberately NO manual-key option while an account is linked.
  // Offering it re-opens the exact defect this whole redesign closed: a key
  // typed by hand can belong to a DIFFERENT Google account than the linked one,
  // and nothing detects it — the failure behind `TLC LATAM`'s placeholder key
  // `aca-se-escribe-1234` and the stream key once typed into a video-id field.
  // Because the combination cannot exist, no cross-account key validation is
  // needed either.
  const modeValue: BindingMode = selectedConn ? 'account' : 'manual';

  // ---- liveStream picker derived state ----
  const linkedStreamId = full?.youtubeLiveStreamId?.trim() || '';
  const hasManualKey = !!full?.youtubeStreamKey?.trim();
  // Default selection: the stored stream if it's in the list; else manual mode
  // when a legacy/manual key exists; else nothing chosen yet.
  // Default to the bound stream when it's in the account's list; otherwise show
  // the "pick a stream" prompt (NOT manual mode — manual is an explicit choice,
  // even for a legacy channel that already has a hand-entered key).
  const streamDefault =
    linkedStreamId && (streams ?? []).some((s) => s.streamId === linkedStreamId)
      ? linkedStreamId
      : NO_STREAM;
  const streamValue = form.streamSel ?? streamDefault;
  // Design rule (design-system.md §4 name-vs-metadata): the option carries the
  // bare NAME only — status is a Pill below, never appended to the label text.
  // Manual entry is no longer an option INSIDE this picker — it is a separate
  // mode (see the SegmentedControl below), so the two paths cannot half-apply.
  const streamOptions: { value: string; label: string }[] = [
    ...(streams ?? []).map((s) => ({ value: s.streamId, label: s.title })),
  ];
  if (linkedStreamId && !streamOptions.some((o) => o.value === linkedStreamId)) {
    streamOptions.unshift({ value: linkedStreamId, label: `Stream ${linkedStreamId.slice(0, 8)}…` });
  }
  // Placeholder prompt when nothing is bound yet, so the combobox is never blank.
  if (streamValue === NO_STREAM) {
    streamOptions.unshift({ value: NO_STREAM, label: '— Elige una transmisión —' });
  }
  const selectedStream = (streams ?? []).find((s) => s.streamId === streamValue);

  // ---- Live broadcast id derived state ----
  const pinned = !!full?.youtubeVideoIdPinned;
  const currentVideoId = full?.youtubeVideoId?.trim() || '';

  // ---- Save validation ----
  // A channel that outputs to YouTube MUST end up with a usable ingest identity.
  // Saving a half-configured one produces exactly the states this redesign
  // exists to remove: a YouTube channel with no key, or an "account" binding
  // with no stream behind it.
  const typedKey = (form.youtubeStreamKey ?? '').trim();
  const boundStreamSel =
    streamValue !== NO_STREAM && streamValue.length > 0 ? streamValue : '';
  // Mirrors YOUTUBE_STREAM_KEY_PATTERN in convex/lib/youtubeIdFormat.ts. Kept
  // as a local copy on purpose: the `@convex` alias resolves ONLY to
  // ../convex/_generated (tsconfig.app.json), and widening it to reach
  // convex/lib is explicitly discouraged there.
  const STREAM_KEY_SHAPE = /^[A-Za-z0-9]{4}(-[A-Za-z0-9]{4}){4}$/;

  let saveBlockedReason: string | null = null;
  if (needsKey) {
    if (modeValue === 'account') {
      if (!boundStreamSel && !linkedStreamId) {
        saveBlockedReason =
          'Elige una transmisión de la cuenta — un canal de YouTube no puede quedar sin clave.';
      }
    } else if (!typedKey && !hasManualKey) {
      saveBlockedReason =
        'Pega la clave de YouTube — un canal de YouTube no puede quedar sin clave.';
    } else if (typedKey && !STREAM_KEY_SHAPE.test(typedKey)) {
      // Only what the operator types NOW is shape-checked. A legacy row holding
      // a malformed key (staging has `aca-se-escribe-1234`) must stay editable,
      // otherwise fixing anything else on that channel is impossible.
      saveBlockedReason =
        'La clave no tiene forma de clave de YouTube (xxxx-xxxx-xxxx-xxxx-xxxx).';
    }
  }

  async function onSave(): Promise<void> {
    setSaving(true);
    setStreamError(null);
    try {
      // (1) Bind the picked liveStream server-side (resolves the real key).
      //     Account mode only — in manual mode there is no stream to bind.
      const picked = modeValue === 'account' ? form.streamSel : undefined;
      if (
        needsKey &&
        picked &&
        picked !== NO_STREAM &&
        picked !== linkedStreamId
      ) {
        try {
          await setLiveStream({
            channelId,
            streamId: picked,
            // Resolve against the freshly selected (still unsaved) account.
            ...(selectedConn && selectedConn !== linkedConn
              ? { connectionId: selectedConn }
              : {}),
          });
        } catch {
          setStreamError('No se pudo vincular la transmisión seleccionada.');
          return; // abort — don't half-save
        }
      }

      // (2) Everything else through the proven updateChannel writer.
      const payload: Record<string, unknown> = { channelId, outputDestination: vOut };

      // Ingest-identity mode. The server owns the consequences: switching to
      // "manual" clears youtubeLiveStreamId unconditionally (channels.ts), so a
      // blank key no longer leaves a stale picked stream id behind. Account mode
      // is re-asserted even when the stream did not change, to upgrade legacy
      // rows the backfill conservatively stamped "manual".
      if (needsKey) {
        if (modeValue === 'manual') {
          payload.youtubeBindingMode = 'manual';
          const ytKey = (form.youtubeStreamKey ?? '').trim();
          if (ytKey) payload.youtubeStreamKey = ytKey;
        } else if (picked || linkedStreamId) {
          payload.youtubeBindingMode = 'account';
        }
      }

      if (needsKey && form.nangoConnSel !== undefined) {
        payload.nangoYoutubeConnectionId =
          form.nangoConnSel === NO_CONNECTION ? '' : form.nangoConnSel;
      }

      // youtubeVideoId is NEVER written from this screen any more — it is
      // resolved from the bound liveStream on every go-live, and YouTube mints a
      // new video id each time, so a hand-set value is stale by the next
      // restart. The one write left is REPAIR: release a legacy pin, because a
      // pinned value permanently suppresses the auto-resolver (it only
      // overwrites unpinned values) — that is how a stream key stayed frozen in
      // the videoId field on staging.
      if (needsKey && pinned) {
        payload.youtubeVideoId = ''; // "" is the CLEAR sentinel (channels.ts)
        payload.youtubeVideoIdPinned = false;
      }

      await updateM.run(payload);
    } catch {
      // updateChannel's wrapper already surfaced its own error toast.
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Field
        label="Destino de salida"
        hint="Origen (O&O) no usa nodo edge ni clave de YouTube. YouTube Live / Ambos requieren ambos."
      >
        <SegmentedControl
          value={vOut}
          reviewed="ok"
          onChange={(v: string) => setForm((f) => ({ ...f, outputDestination: v as OutputDestination }))}
          options={[
            { value: 'o_and_o', label: 'Origen (O&O)' },
            { value: 'youtube_live', label: 'YouTube Live' },
            { value: 'both', label: 'Ambos' },
          ]}
        />
      </Field>

      {needsKey && (
        <Field
          label={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              Cuenta de YouTube (OAuth)
              {/* Accounts are created by an OAuth consent flow, which cannot
                 happen inside this form — so this links to the dedicated
                 surface rather than trying to inline it. */}
              <Btn
                variant="text"
                size="sm"
                reviewed="wired"
                // Routing is real paths through the `lib/nav` shim → react-router
                // (see routes.tsx). An `href="#/settings/…"` only appends a hash
                // fragment to the current URL — it does NOT navigate.
                //
                // stopPropagation because `Field` renders its label inside a
                // <label>, so the click would otherwise ALSO activate the
                // wrapped Select and pop its dropdown open behind the change.
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigate('/settings/youtube-accounts');
                }}
              >
                Gestionar cuentas
              </Btn>
            </span>
          }
          hint="La cuenta de Google que autoriza la lectura del estado en vivo y el reporte de ingresos. Una cuenta puede respaldar varios canales."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {conns === undefined ? (
              <Skeleton h={40} w={280} />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Select
                      full
                      reviewed="wired"
                      value={connValue}
                      onChange={(v: string) =>
                        // Changing the account invalidates any stream picked
                        // from the previous account's list.
                        setForm((f) => ({ ...f, nangoConnSel: v, streamSel: undefined }))
                      }
                      options={connOptions}
                    />
                  </div>
                  <Btn
                    size="sm"
                    variant="ghost"
                    icon="refresh"
                    reviewed="wired"
                    title="Actualizar la lista de cuentas de Nango"
                    loading={connsRefreshing}
                    onClick={() => {
                      setConnsRefreshing(true);
                      setConnsReload((k) => k + 1);
                    }}
                  />
                </div>
                {conns === null ? (
                  <Pill tone="warn" size="sm">No se pudieron cargar las cuentas — verifica la conexión con Nango</Pill>
                ) : conns.length === 0 ? (
                  <Pill tone="neutral" size="sm">No hay cuentas de YouTube conectadas todavía</Pill>
                ) : linkedConn ? (
                  <Pill tone="good" size="sm" dot>Cuenta vinculada</Pill>
                ) : (
                  <Pill tone="warn" size="sm">Sin cuenta — el estado en vivo no estará disponible</Pill>
                )}
              </>
            )}
          </div>
        </Field>
      )}

      {needsKey && (
        <Field
          label="Identidad de ingesta en YouTube"
          hint="Con cuenta vinculada, elige una transmisión: la clave se resuelve sola desde esa misma cuenta, así que no pueden quedar desalineadas. Sin cuenta vinculada se pega la clave a mano y CIRCA no puede identificar el canal en YouTube."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!selectedConn && (
              <Pill tone="neutral" size="sm">
                Sin cuenta vinculada — solo clave manual
              </Pill>
            )}

            {modeValue === 'account' ? (
              streams === undefined ? (
                <Skeleton h={40} w={280} />
              ) : (
                <>
                  <Select
                    full
                    reviewed="wired"
                    value={streamValue}
                    onChange={(v: string) => setForm((f) => ({ ...f, streamSel: v }))}
                    options={streamOptions}
                  />
                  {streams === null && (
                    // Do NOT point at manual entry here: with an account linked
                    // there is no manual option (strict either/or). The only
                    // way out is to unlink the account above.
                    <Pill tone="warn" size="sm">
                      No se pudieron cargar las transmisiones de esta cuenta — revisa la conexión, o quita la cuenta arriba para ingresar la clave a mano
                    </Pill>
                  )}
                  {/* Status as a tag/box, never appended to the option name
                     (design-system.md §4 name-vs-metadata). */}
                  {streamValue === NO_STREAM && (
                    <Pill tone="warn" size="sm">Sin transmisión — requerida para YouTube</Pill>
                  )}
                  {selectedStream && (
                    <Pill tone={selectedStream.streamStatus === 'active' ? 'good' : 'neutral'} size="sm" dot={selectedStream.streamStatus === 'active'}>
                      {selectedStream.streamStatus === 'active' ? 'En vivo' : 'Inactiva'}
                    </Pill>
                  )}
                  {!selectedStream && linkedStreamId && (
                    <Pill tone="good" size="sm" dot>Vinculada</Pill>
                  )}
                </>
              )
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {hasManualKey ? (
                  <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)', textTransform: 'none' }}>
                    Clave actual: {maskKey(full?.youtubeStreamKey ?? '')}
                  </span>
                ) : (
                  <Pill tone="warn" size="sm">Sin clave — requerida para YouTube</Pill>
                )}
                <Input
                  value={form.youtubeStreamKey ?? ''}
                  onChange={(v: string) => setForm((f) => ({ ...f, youtubeStreamKey: v }))}
                  placeholder={hasManualKey ? '•••••••• — escribe una nueva para reemplazar' : 'Pega tu clave de YouTube'}
                  reviewed="ok"
                />
                {/* Honest state (design-system.md §4b): name what this mode
                   costs, rather than letting it look equivalent to the picker. */}
                <Pill tone="warn" size="sm">
                  Sin identidad verificable — sin estado de ingesta por canal ni reporte de anuncios a terceros
                </Pill>
              </div>
            )}
          </div>
        </Field>
      )}

      {needsKey && modeValue === 'account' && (
        <Field
          label="ID de transmisión en vivo (video)"
          hint="Siempre automático. Se resuelve desde la transmisión vinculada cada vez que el canal sale al aire; no es editable porque YouTube emite un ID de video NUEVO en cada salida, así que cualquier valor fijado a mano queda obsoleto al primer reinicio."
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pill tone="neutral" size="sm" dot>Automático</Pill>
            <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)', textTransform: 'none' }}>
              {currentVideoId || '— sin transmisión en vivo detectada —'}
            </span>
            {pinned && (
              // A legacy pin BLOCKS the auto-resolver, which only overwrites an
              // unpinned value — this is how a stream key ended up frozen in the
              // videoId field on staging. Saving clears it (see onSave).
              <Pill tone="warn" size="sm">
                Valor fijado antiguo — se liberará al guardar
              </Pill>
            )}
          </div>
        </Field>
      )}

      {streamError && <Pill tone="bad" size="sm">{streamError}</Pill>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Honest state: say WHY the action is unavailable, never just grey it
           out (design-system.md §4b). */}
        {saveBlockedReason && (
          <Pill tone="warn" size="sm">{saveBlockedReason}</Pill>
        )}
        <div>
          <Btn
            variant="solid"
            reviewed="wired"
            loading={saving || updateM.loading}
            disabled={saveBlockedReason !== null}
            title={saveBlockedReason ?? undefined}
            onClick={() => { void onSave(); }}
          >
            Guardar destinos
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default ChannelDestinations;
